/**
 * Web adapter over the shared product-scope catalog (`@dashone/user-scopes`).
 *
 * The module catalog — keys, labels, routes, and the per-module scope helpers —
 * is the shared source of truth, re-exported here so existing web imports
 * (invite/edit modals, sidebar nav, route guards) keep resolving from
 * `constants/moduleScopes`. This module adds the web-only access-decision
 * helpers. An empty or missing country list for a module means no access to it.
 */

export {
  MODULE_SCOPES,
  MODULE_SCOPE_KEYS,
  ROUTE_TO_MODULE,
  emptyProductScopes,
} from "@dashone/user-scopes";

/** Roles that bypass per-module scope checks (full access). */
export const ADMIN_ROLES = ["super_admin"];

/**
 * Whether a role has unrestricted access (admins bypass module scoping).
 * @param {string | undefined | null} role
 * @returns {boolean}
 */
export const isAdminRole = (role) => ADMIN_ROLES.includes(role);

/**
 * Pure check: does the given scope grant access to a module?
 *
 * Admins (or a null product_scopes, which is how admins are stored) always
 * have access. Otherwise the module's country list must be non-empty.
 *
 * @param {Record<string, string[]> | null | undefined} productScopes
 * @param {string | undefined | null} role
 * @param {string} moduleKey
 * @returns {boolean}
 */
export const hasModuleAccess = (productScopes, role, moduleKey) => {
  if (isAdminRole(role)) return true;
  // Non-admins always have a product_scopes object; a null/absent object for a
  // non-admin means no granular grants were configured -> deny (fail-closed).
  if (!productScopes || typeof productScopes !== "object") return false;
  const countries = productScopes[moduleKey];
  return Array.isArray(countries) && countries.length > 0;
};

/**
 * Pure check: does the given scope grant a module for one country?
 *
 * Same rule as {@link hasModuleAccess}, narrowed to `country`. Admins pass;
 * anyone else needs `country` in the module's list.
 *
 * @param {Record<string, string[]> | null | undefined} productScopes
 * @param {string | undefined | null} role
 * @param {string} moduleKey
 * @param {string | undefined | null} country
 * @returns {boolean}
 */
export const hasModuleCountryAccess = (productScopes, role, moduleKey, country) => {
  if (isAdminRole(role)) return true;
  if (!country || !productScopes || typeof productScopes !== "object") return false;
  const countries = productScopes[moduleKey];
  return Array.isArray(countries) && countries.includes(country);
};
